import axios from 'axios'
import Axhelper from '@/lib/Axhelper'
import { normalizeAssignedTrainers, normalizeRozliczono } from '@/pages/centrumSzkolen/Szkolenia/details/trainerDetailsHelpers'

const getRelationId = value => {
	if (!value) return ''
	if (typeof value === 'object') {
		return `${value?._id || value?.id || ''}`.trim()
	}
	return `${value}`.trim()
}

const parseCertificates = value => {
	if (Array.isArray(value)) {
		return value
			.map(item => ({
				certificateId: `${item?.certificateId ?? item?.certificationId ?? ''}`.trim(),
				certificateName: `${item?.certificateName ?? item?.name ?? item?.nazwa ?? ''}`.trim(),
			}))
			.filter(item => item.certificateId)
	}

	if (typeof value === 'string' && value.trim()) {
		try {
			return parseCertificates(JSON.parse(value))
		} catch {
			return []
		}
	}

	return []
}

const buildCertificatesPayload = data => {
	if (Array.isArray(data?.certificates) && data.certificates.length) {
		return parseCertificates(data.certificates)
	}

	return (Array.isArray(data?.requiredCertificationIds) ? data.requiredCertificationIds : [])
		.filter(Boolean)
		.map(certificateId => ({
			certificateId: String(certificateId),
			certificateName: '',
		}))
}

const normalizeTrainingFiles = files => (
	Array.isArray(files) ? files : []
).map(item => ({
	fileUuid: `${item?.fileUuid || item?._id || ''}`.trim(),
	filePath: `${item?.filePath || ''}`.trim(),
	originalName: `${item?.originalName || item?.fileName || item?.name || ''}`.trim(),
})).filter(item => item.fileUuid)

const extractPlacowkaFields = training => {
	const embedded = training?.placowkaId && typeof training.placowkaId === 'object'
		? training.placowkaId
		: null

	return {
		placowkaId: getRelationId(training?.placowkaId) || null,
		placowkaName: `${training?.placowkaName || embedded?.nazwa || ''}`.trim(),
		placowkaAddress: `${training?.placowkaAddress || embedded?.adres || ''}`.trim(),
		id_miasto: training?.id_miasto || embedded?.id_miasto || null,
		id_gmina: training?.id_gmina || embedded?.id_gmina || null,
		id_powiat: training?.id_powiat || embedded?.id_powiat || null,
		id_wojewodztwo: training?.id_wojewodztwo || embedded?.id_wojewodztwo || null,
		id_typ: training?.id_typ || embedded?.id_typ || null,
	}
}

const mapApiTrainingToUi = training => {
	if (!training || typeof training !== 'object') return training

	const placowkaFields = extractPlacowkaFields(training)
	const certificates = parseCertificates(training.certificates)
	const requiredCertificationIds = Array.isArray(training.requiredCertificationIds) && training.requiredCertificationIds.length
		? training.requiredCertificationIds
		: certificates.map(item => item.certificateId).filter(Boolean)
	const adres = `${training.adres ?? training.ulica ?? ''}`.trim()
	const miasto = `${training.miasto ?? ''}`.trim()
	const kod_pocztowy = `${training.kod_pocztowy || ''}`.trim()
	const nazwa = training.nazwa || training.trainingName || ''
	const data_rozpoczecia = training.data_rozpoczecia || training.trainingDate || ''
	const data_zakonczenia = training.data_zakonczenia || training.trainingEndDate || data_rozpoczecia
	const locationParts = [adres, miasto, kod_pocztowy].filter(Boolean)
	const assignedTrainers = normalizeAssignedTrainers(training)
	const trainerNames = assignedTrainers.map(item => item.trainerName).filter(Boolean)

	return {
		...training,
		...placowkaFields,
		assignedTrainers,
		trainerNames,
		trainerName: trainerNames.join(', '),
		selectedTrainerIds: assignedTrainers.map(item => item.trainerId).filter(Boolean),
		trainerCompensation: assignedTrainers.map(item => ({
			trainerId: item.trainerId,
			trainerName: item.trainerName,
			hourlyRateGross: `${item.stawka_godzinowa ?? ''}`,
			extraAmountGross: '',
		})),
		nazwa,
		requiredCertificationIds,
		certificates,
		data_rozpoczecia,
		data_zakonczenia,
		startTime: training.startTime || training.godzina_rozpoczecia || '',
		endTime: training.endTime || training.godzina_zakonczenia || '',
		godzina_rozpoczecia: training.godzina_rozpoczecia || training.startTime || '',
		godzina_zakonczenia: training.godzina_zakonczenia || training.endTime || '',
		remarks: training.remarks || training.uwagi || '',
		uwagi: training.uwagi || training.remarks || '',
		ulica: adres,
		adres,
		miasto,
		kod_pocztowy,
		location: training.location || locationParts.join(', '),
		trainingFiles: normalizeTrainingFiles(training.trainingfiles || training.trainingFiles),
		content: normalizeTrainingContent(training.content),
	}
}

const normalizeTrainingContent = value => {
	if (!Array.isArray(value)) {
		if (typeof value === 'string' && value.trim()) {
			try {
				return normalizeTrainingContent(JSON.parse(value))
			} catch {
				return [value.trim()]
			}
		}
		return []
	}

	return value
		.map(item => {
			if (typeof item === 'string') return item.trim()
			return `${item?.name ?? item?.value ?? item?.label ?? ''}`.trim()
		})
		.filter(Boolean)
}

const normalizeExtraCosts = costs => (
	Array.isArray(costs) ? costs : []
).map(item => ({
	_id: item?._id || `travel-cost-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
	title: item?.title || '',
	amountGross: item?.amountGross ?? '',
	note: item?.note || '',
}))

const normalizeTravelPlans = travelPlans => (
	Array.isArray(travelPlans) ? travelPlans : []
).map(item => ({
	_id: item?._id || `travel-plan-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
	driverTrainerId: item?.driverTrainerId || null,
	driverTrainerName: item?.driverTrainerName || '',
	passengerTrainerIds: Array.isArray(item?.passengerTrainerIds) ? item.passengerTrainerIds.filter(Boolean) : [],
	passengerTrainerNames: Array.isArray(item?.passengerTrainerNames) ? item.passengerTrainerNames.filter(Boolean) : [],
	startDate: item?.startDate || '',
	endDate: item?.endDate || '',
	dailyReturn: item?.dailyReturn !== false,
	estimatedOneWayKm: item?.estimatedOneWayKm ?? '',
	ratePerKm: item?.ratePerKm ?? 1,
	extraCosts: normalizeExtraCosts(item?.extraCosts),
}))

const normalizeCompletionProofFiles = files => (
	Array.isArray(files) ? files : []
).map(item => ({
	_id: item?._id || `training-proof-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
	fileName: item?.fileName || item?.name || '',
	fileSize: item?.fileSize ?? item?.size ?? '',
	fileType: item?.fileType || item?.type || '',
	fileUrl: item?.fileUrl || '',
	uploadedAt: item?.uploadedAt || '',
	uploadedByTrainerId: item?.uploadedByTrainerId || null,
	uploadedByTrainerName: item?.uploadedByTrainerName || '',
	accepted: item?.accepted === true,
	acceptedAt: item?.acceptedAt || '',
	acceptedBy: item?.acceptedBy || '',
}))

class TrainingsService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG?.API_URL || ''
		this.endpoints = {
			trainings: '/api/szkolenia/filters',
			trainingsDetails: '/api/szkolenia/details',
			trainingsKatalog: '/api/szkolenia/filters',
			trainingsCreate: '/api/szkolenia',
			trainingsUpdate: '/api/szkolenia',
			trainingsDelete: '/api/szkolenia',
			uploadTrainingFile: '/api/szkolenia/uploadtrainingfile',
			downloadTrainingFile: '/api/szkolenia/downloadtrainingfile',
			deleteTrainingFile: '/api/szkolenia/deletetrainingfile',
			getContentOptions: '/api/szkolenia/getcontentoptions',
		}
	}

	async getAll(options = {}) {
		try {
			const {
				limit = 25,
				sortColumn = 'trainingDate',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = this._buildParams({ limit, sortColumn, sortDirection, filters })
			const url = `${this.endpoints.trainings}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			return this._buildListResponse(response)
		} catch (error) {
			console.error('TrainingsService.getAll error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch trainings',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getById(trainingId) {
		try {
			if (!trainingId) {
				return {
					data: null,
					error: 'Invalid training ID provided',
				}
			}

			const url = `${this.endpoints.trainingsDetails}/${encodeURIComponent(trainingId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: null,
					error: response.error,
				}
			}

			return {
				data: this._normalizeTrainings([response.data])[0] || response.data || null,
				error: null,
			}
		} catch (error) {
			console.error('TrainingsService.getById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch training',
			}
		}
	}

	async search(searchTerm, options = {}) {
		try {
			const trimmed = `${searchTerm || ''}`.trim()
			if (!trimmed) {
				return {
					data: [],
					error: 'Invalid search term provided',
					totalCount: 0,
					hasMore: false,
				}
			}

			const {
				limit = 25,
				sortColumn = 'trainingDate',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = this._buildParams({ limit, sortColumn, sortDirection, filters })
			const url = `${this.endpoints.trainings}?searchText=${encodeURIComponent(trimmed)}&${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			return this._buildListResponse(response)
		} catch (error) {
			console.error('TrainingsService.search error:', error)
			return {
				data: [],
				error: error.message || 'Failed to search trainings',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getContentOptions() {
		try {
			const response = await Axhelper.sendGetAsync(this.endpoints.getContentOptions)

			if (response.error) {
				return { data: [], error: response.error }
			}

			const items = Array.isArray(response.data)
				? response.data
				: response.data?.content || response.data?.options || []

			return { data: this._normalizeContentOptions(items), error: null }
		} catch (error) {
			console.error('TrainingsService.getContentOptions error:', error)
			return { data: [], error: error.message || 'Failed to fetch training content options' }
		}
	}

	async getTrainingCatalog(options = {}) {
		try {
			const {
				limit = 5,
				sortColumn = 'name',
				sortDirection = 'asc',
				filters = {},
			} = options

			const params = this._buildParams({ limit, sortColumn, sortDirection, filters })
			const url = `${this.endpoints.trainingsKatalog}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			return this._buildCatalogListResponse(response)
		} catch (error) {
			console.error('TrainingsService.getTrainingCatalog error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch training catalog',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async create(trainingData) {
		try {
			if (!this._getTrainingName(trainingData)) {
				return { success: false, error: 'Training name is required', data: null }
			}

			const response = await Axhelper.sendPostAsync(
				this.endpoints.trainingsCreate,
				this._buildCreateFormData(trainingData)
			)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					data: null,
				}
			}

			const normalized = this._normalizeTrainings([response.data])[0] || response.data || null

			return {
				success: true,
				error: null,
				data: normalized,
			}
		} catch (error) {
			console.error('TrainingsService.create error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create training',
				data: null,
			}
		}
	}

	async update(trainingId, trainingData) {
		try {
			if (!trainingId) {
				return { success: false, error: 'Invalid training ID provided', data: null }
			}
			if (!this._getTrainingName(trainingData)) {
				return { success: false, error: 'Training name is required', data: null }
			}

			const url = `${this.endpoints.trainingsUpdate}/${encodeURIComponent(trainingId)}`
			const response = await Axhelper.sendPutAsync(url, trainingData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					data: null,
				}
			}

			const normalized = this._normalizeTrainings([response.data])[0] || response.data || null

			return {
				success: true,
				error: null,
				data: normalized,
			}
		} catch (error) {
			console.error('TrainingsService.update error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update training',
				data: null,
			}
		}
	}

	async delete(trainingId) {
		try {
			if (!trainingId) {
				return { success: false, error: 'Invalid training ID provided', message: null }
			}

			const url = `${this.endpoints.trainingsDelete}/${encodeURIComponent(trainingId)}`
			const response = await Axhelper.sendDeleteAsync(url)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					message: null,
				}
			}

			return {
				success: true,
				error: null,
				message: response.data || 'Training deleted successfully.',
			}
		} catch (error) {
			console.error('TrainingsService.delete error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete training',
				message: null,
			}
		}
	}

	async uploadTrainingFile(trainingId, file, onUploadProgress) {
		try {
			if (!trainingId) {
				return { success: false, error: 'Invalid training ID provided', data: null }
			}
			if (!file) {
				return { success: false, error: 'No file provided', data: null }
			}

			const formData = new FormData()
			formData.append('file', file)
			const url = `${this.endpoints.uploadTrainingFile}/${encodeURIComponent(trainingId)}`
			const response = await Axhelper.sendUploadAsync(url, formData, onUploadProgress)

			if (response.error) {
				return { success: false, error: response.error, data: null }
			}

			const normalized = this._normalizeTrainings([response.data])[0] || response.data || null
			return { success: true, error: null, data: normalized }
		} catch (error) {
			console.error('TrainingsService.uploadTrainingFile error:', error)
			return {
				success: false,
				error: error.message || 'Failed to upload training file',
				data: null,
			}
		}
	}

	async downloadTrainingFile(trainingId, fileUuid) {
		try {
			if (!trainingId || !fileUuid) {
				return { success: false, error: 'Invalid training file reference', blob: null, filename: null }
			}

			const url = `${this.endpoints.downloadTrainingFile}/${encodeURIComponent(trainingId)}/${encodeURIComponent(fileUuid)}`
			const response = await this._downloadBlob(url)

			if (response.error) {
				return { success: false, error: response.error, blob: null, filename: null }
			}

			const filename = this._extractDownloadFilename(
				response.headers,
				fileUuid,
				trainingId
			)

			return { success: true, error: null, blob: response.blob, filename }
		} catch (error) {
			console.error('TrainingsService.downloadTrainingFile error:', error)
			return {
				success: false,
				error: error.message || 'Failed to download training file',
				blob: null,
				filename: null,
			}
		}
	}

	async deleteTrainingFile(trainingId, fileUuid) {
		try {
			if (!trainingId || !fileUuid) {
				return { success: false, error: 'Invalid training file reference', data: null }
			}

			const url = `${this.endpoints.deleteTrainingFile}/${encodeURIComponent(trainingId)}/${encodeURIComponent(fileUuid)}`
			const response = await Axhelper.sendDeleteAsync(url)

			if (response.error) {
				return { success: false, error: response.error, data: null }
			}

			const normalized = this._normalizeTrainings([response.data])[0] || response.data || null
			return { success: true, error: null, data: normalized }
		} catch (error) {
			console.error('TrainingsService.deleteTrainingFile error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete training file',
				data: null,
			}
		}
	}

	async updateCompletionProofFiles(trainingId, completionProofFiles, status = null) {
		try {
			if (!trainingId) {
				return { success: false, error: 'Invalid training ID provided', data: null }
			}

			const trainingResult = await this.getById(trainingId)
			const training = trainingResult?.data
			if (!training || trainingResult?.error) {
				return { success: false, error: trainingResult?.error || 'Training not found', data: null }
			}

			const result = await this.update(trainingId, {
				nazwa: training.nazwa || training.trainingName || '',
				placowkaId: training.placowkaId || null,
				placowkaName: training.placowkaName || '',
				placowkaAddress: training.placowkaAddress || '',
				id_miasto: training.id_miasto || null,
				id_gmina: training.id_gmina || null,
				id_powiat: training.id_powiat || null,
				id_wojewodztwo: training.id_wojewodztwo || null,
				id_typ: training.id_typ || null,
				requiredCertificationIds: Array.isArray(training.requiredCertificationIds) ? training.requiredCertificationIds : [],
				assignedTrainers: normalizeAssignedTrainers(training),
				data_rozpoczecia: training.data_rozpoczecia || training.trainingDate || '',
				data_zakonczenia: training.data_zakonczenia || training.trainingEndDate || training.data_rozpoczecia || '',
				location: training.location || '',
				status: status || training.status || 'new',
				startTime: training.startTime || '',
				endTime: training.endTime || '',
				remarks: training.remarks || '',
				travelPlans: Array.isArray(training.travelPlans) ? training.travelPlans : [],
				completionProofFiles: normalizeCompletionProofFiles(completionProofFiles),
			})

			return result
		} catch (error) {
			console.error('TrainingsService.updateCompletionProofFiles error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update completion proof files',
				data: null,
			}
		}
	}

	_normalizeTrainings(trainings) {
		return (Array.isArray(trainings) ? trainings : []).map(training => {
			const mapped = mapApiTrainingToUi(training)
			const placowkaFields = extractPlacowkaFields(mapped)
			const trainerNames = Array.isArray(mapped.trainerNames)
				? mapped.trainerNames.filter(Boolean)
				: mapped.trainerName
				? [mapped.trainerName]
				: []

			return {
				...mapped,
				...placowkaFields,
				placowkaId: placowkaFields.placowkaId || null,
				placowkaName: placowkaFields.placowkaName || mapped.placowkaNameFallback || '—',
				placowkaAddress: placowkaFields.placowkaAddress || '',
				placowkaMiasto: placowkaFields.id_miasto?.nazwa || mapped.placowkaMiasto || '',
				placowkaGmina: placowkaFields.id_gmina?.nazwa || mapped.placowkaGmina || '',
				placowkaPowiat: placowkaFields.id_powiat?.nazwa || mapped.placowkaPowiat || '',
				placowkaWojewodztwo: placowkaFields.id_wojewodztwo?.nazwa || mapped.placowkaWojewodztwo || '',
				placowkaTyp: placowkaFields.id_typ?.typ || mapped.placowkaTyp || '',
				trainerNames,
				trainerName: trainerNames.join(', '),
				data_zakonczenia: mapped.data_zakonczenia || mapped.data_rozpoczecia || '',
				travelPlans: normalizeTravelPlans(mapped.travelPlans),
				trainingFiles: normalizeTrainingFiles(mapped.trainingfiles || mapped.trainingFiles),
				completionProofFiles: normalizeCompletionProofFiles(mapped.completionProofFiles),
			}
		})
	}

	_buildParams({ limit, sortColumn, sortDirection, filters }) {
		const params = new URLSearchParams({
			sortcol: sortColumn,
			sortdir: sortDirection,
			limit: String(limit),
		})

		Object.entries(filters || {}).forEach(([key, value]) => {
			if (key === 'searchText' || value === undefined || value === null || value === '') return
			params.append(key, String(value))
		})

		return params
	}

	_buildListResponse(response) {
		const rows = this._normalizeTrainings(response.data)
		const totalCount = response.headers?.['x-total-count']
			? parseInt(response.headers['x-total-count'], 10)
			: rows.length

		return {
			data: rows,
			error: null,
			totalCount,
			hasMore: rows.length < totalCount,
			headers: response.headers,
		}
	}

	_buildCatalogListResponse(response) {
		const rows = this._normalizeCatalogItems(response.data)
		const totalCount = response.headers?.['x-total-count']
			? parseInt(response.headers['x-total-count'], 10)
			: rows.length

		return {
			data: rows,
			error: null,
			totalCount,
			hasMore: rows.length < totalCount,
			headers: response.headers,
		}
	}

	_normalizeContentOptions(items = []) {
		return (Array.isArray(items) ? items : []).map(item => {
			if (typeof item === 'string') {
				const value = item.trim()
				return value ? { value, label: value } : null
			}

			const value = `${item?.nazwa ?? item?.name ?? item?.value ?? ''}`.trim()
			return value ? { value, label: value } : null
		}).filter(Boolean)
	}

	_normalizeCatalogItems(items = []) {
		return (Array.isArray(items) ? items : []).map(item => {
			if (typeof item === 'string') {
				const name = item.trim()
				return name ? { _id: name, name } : null
			}

			const name = `${item?.nazwa ?? item?.name ?? item?.trainingName ?? ''}`.trim()
			const id = `${item?._id ?? item?.id ?? name}`.trim()

			return name ? { _id: id, name } : null
		}).filter(Boolean)
	}

	async _downloadBlob(url) {
		try {
			const token = localStorage.getItem('token')
			const response = await axios.get(url, {
				responseType: 'blob',
				withCredentials: true,
				headers: { token },
			})

			return { error: null, blob: response.data, headers: response.headers }
		} catch (error) {
			const errorMessage = error.response?.data || error.message || 'Download failed'
			return { error: errorMessage, blob: null, headers: null }
		}
	}

	_extractDownloadFilename(headers, fileUuid, trainingId) {
		const defaultFilename = `training-file-${trainingId}-${fileUuid}`

		if (!headers) return defaultFilename

		const disposition = headers['content-disposition'] || ''
		const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
		if (utf8Match?.[1]) {
			return decodeURIComponent(utf8Match[1])
		}

		const regularMatch = disposition.match(/filename="?([^";]+)"?/i)
		if (regularMatch?.[1]) {
			return regularMatch[1]
		}

		return defaultFilename
	}

	_appendTrainingBaseFields(params, data = {}) {
		const certificates = buildCertificatesPayload(data)
		const adres = `${data.ulica ?? data.adres ?? ''}`.trim()

		params.append('nazwa', data.nazwa ?? '')
		params.append('placowkaId', getRelationId(data.placowkaId))
		params.append('placowkaName', data.placowkaName ?? '')
		params.append('status', data.status ?? 'planowane')
		params.append('data_rozpoczecia', data.data_rozpoczecia ?? '')
		params.append('data_zakonczenia', data.data_zakonczenia ?? data.data_rozpoczecia ?? '')
		params.append('godzina_rozpoczecia', data.startTime ?? data.godzina_rozpoczecia ?? '')
		params.append('godzina_zakonczenia', data.endTime ?? data.godzina_zakonczenia ?? '')
		params.append('adres', adres)
		params.append('miasto', data.miasto ?? '')
		params.append('kod_pocztowy', data.kod_pocztowy ?? '')
		params.append('uwagi', data.remarks ?? data.uwagi ?? '')
		params.append('g_placeid', data.g_placeid ?? '')

		if (data.g_lat !== '' && data.g_lat != null) {
			params.append('g_lat', String(data.g_lat))
		}
		if (data.g_long !== '' && data.g_long != null) {
			params.append('g_long', String(data.g_long))
		}
		if (certificates.length) {
			params.append('certificates', JSON.stringify(certificates))
		}

		const content = normalizeTrainingContent(data.content)
		if (content.length) {
			params.append('content', JSON.stringify(content))
		}
	}

	_serializeAssignedTrainers(assignedTrainers = []) {
		return JSON.stringify((Array.isArray(assignedTrainers) ? assignedTrainers : []).map(item => ({
			trainerId: item?.trainerId || '',
			trainerName: item?.trainerName || '',
			miasto: item?.miasto ?? '',
			odleglosc_km: item?.odleglosc_km ?? '',
			stawka_godzinowa: item?.stawka_godzinowa ?? '',
			stawka_za_kilometr: item?.stawka_za_kilometr ?? 1,
			ilosc_godzin: item?.ilosc_godzin ?? '',
			do_wyplaty: item?.do_wyplaty ?? '',
			rozliczono: normalizeRozliczono(item?.rozliczono),
			inne_koszty: (Array.isArray(item?.inne_koszty) ? item.inne_koszty : []).map(cost => ({
				tytul: `${cost?.tytul ?? ''}`.trim(),
				kwota_brutto: cost?.kwota_brutto ?? '',
				notatka: `${cost?.notatka ?? ''}`.trim(),
			})),
		})))
	}

	_appendAssignedTrainers(params, assignedTrainers) {
		if (!Array.isArray(assignedTrainers) || !assignedTrainers.length) {
			return
		}

		params.append('assignedTrainers', this._serializeAssignedTrainers(assignedTrainers))
	}

	_buildCreateFormData(data = {}) {
		const params = new URLSearchParams()
		this._appendTrainingBaseFields(params, data)

		const assignedTrainers = Array.isArray(data.assignedTrainers)
			? data.assignedTrainers
			: normalizeAssignedTrainers(data)
		this._appendAssignedTrainers(params, assignedTrainers)

		return params
	}

	_buildUpdateFormData(data = {}) {
		const params = new URLSearchParams()
		this._appendTrainingBaseFields(params, data)

		if (Array.isArray(data.assignedTrainers)) {
			params.append('assignedTrainers', this._serializeAssignedTrainers(data.assignedTrainers))
		}

		return params
	}

	_getTrainingName(data = {}) {
		return `${data.nazwa ?? ''}`.trim()
	}
}

export default new TrainingsService()
